type ObjectKeys<T> = T extends object ? keyof T : never

type ObjectPropertyPathLevel2<Object extends object> = {
  [PropertyKey1 in keyof Object]: ObjectKeys<Object[PropertyKey1]> extends never
    ? never
    : [PropertyKey1, ObjectKeys<Object[PropertyKey1]>]
}[keyof Object]

type ObjectPropertyPathLevel3<Object extends object> = {
  [PropertyKey1 in keyof Object]: Object[PropertyKey1] extends object
    ? {
        [PropertyKey2 in keyof Object[PropertyKey1]]: ObjectKeys<
          Object[PropertyKey1][PropertyKey2]
        > extends never
          ? never
          : [
              PropertyKey1,
              PropertyKey2,
              ObjectKeys<Object[PropertyKey1][PropertyKey2]>,
            ]
      }[keyof Object[PropertyKey1]]
    : never
}[keyof Object]

export type ObjectPropertyPath<Object extends object> =
  | keyof Object
  | ObjectPropertyPathLevel2<Object>
  | ObjectPropertyPathLevel3<Object>

/**
 * Creates a shallow copy of `object` where properties described by `keys` are
 * replaced with values from `newObject`.
 *
 * @param object Source object.
 * @param newObject Object containing the replacement values for the properties described by `keys`.
 * @param keys Key or tuple of keys describing the nested path to replace.
 */
export const setObjectProperty = <Object extends object>(
  object: Object,
  newObject: Object,
  propertyKeys: ObjectPropertyPath<Object>,
): Object => {
  const objectRecord = object as Record<PropertyKey, unknown>
  const newObjectRecord = newObject as Record<PropertyKey, unknown>

  if (!Array.isArray(propertyKeys)) {
    return { ...object, [propertyKeys]: newObjectRecord[propertyKeys] }
  } else if (propertyKeys.length === 2) {
    const [propertyKey1, propertyKey2] = propertyKeys
    const objectLevel = objectRecord?.[propertyKey1] as Record<
      PropertyKey,
      unknown
    >
    const newObjectLevel = newObjectRecord?.[propertyKey1] as Record<
      PropertyKey,
      unknown
    >

    return {
      ...object,
      [propertyKey1]: {
        ...objectLevel,
        [propertyKey2]: newObjectLevel?.[propertyKey2],
      },
    }
  } else if (propertyKeys.length === 3) {
    const [propertyKey1, propertyKey2, propertyKey3] = propertyKeys
    const objectLevel1 = objectRecord?.[propertyKey1] as Record<
      PropertyKey,
      unknown
    >
    const newObjectLevel1 = newObjectRecord?.[propertyKey1] as Record<
      PropertyKey,
      unknown
    >
    const objectLevel2 = objectLevel1?.[propertyKey2] as Record<
      PropertyKey,
      unknown
    >
    const newObjectLevel2 = newObjectLevel1?.[propertyKey2] as Record<
      PropertyKey,
      unknown
    >

    return {
      ...object,
      [propertyKey1]: {
        ...objectLevel1,
        [propertyKey2]: {
          ...objectLevel2,
          [propertyKey3]: newObjectLevel2?.[propertyKey3],
        },
      },
    }
  }
  throw new Error('Invalid keys length')
}
